import { useState } from 'react';
import dayjs from 'dayjs';

interface Checkin {
  created_at: string;
  late_status: string;
  meal_allowance: number;
  exemption_applied: boolean;
}

interface CalendarViewProps {
  checkins?: Checkin[];
}

const CalendarView = ({ checkins = [] }: CalendarViewProps) => {
  const [currentDate, setCurrentDate] = useState(dayjs());

  // Ensure checkins is always an array
  const checkinArray = Array.isArray(checkins) ? checkins : [];

  const renderCalendar = () => {
    const firstDay = currentDate.startOf('month');
    const lastDay = currentDate.endOf('month');
    const startDay = firstDay.startOf('week');
    const endDay = lastDay.endOf('week');

    const weeks = [];
    let days = [];
    let day = startDay;

    while (day.isBefore(endDay) || day.isSame(endDay, 'day')) {
      const checkin = checkinArray.find(c => 
        dayjs(c.created_at).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
      );

      const isCurrentMonth = day.month() === currentDate.month();
      const isToday = day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');

      let statusColor = '';
      let statusEmoji = '';

      if (checkin) {
        if (checkin.late_status === 'perfect_on_time') {
          statusColor = 'bg-green-800';
          statusEmoji = '🌟';
        } else if (checkin.late_status === 'on_time') {
          statusColor = 'bg-green-500';
          statusEmoji = '✅';
        } else if (checkin.late_status === 'late_10') {
          statusColor = checkin.exemption_applied ? 'bg-blue-500' : 'bg-amber-500';
          statusEmoji = '⏰';
        } else if (checkin.late_status === 'late_15') {
          statusColor = 'bg-red-500';
          statusEmoji = '⚠️';
        }
      }

      days.push(
        <div 
          key={day.format('YYYY-MM-DD')} 
          className={`p-1 sm:p-2 border border-gray-200 rounded min-h-[45px] sm:min-h-[60px] md:min-h-[80px] relative transition-all hover:border-blue-400 ${
            isCurrentMonth ? 'bg-white' : 'bg-gray-50 opacity-70'
          } ${isToday ? 'border-blue-500 bg-blue-50' : ''}`}
        >
          <div className="font-medium text-gray-700 mb-0.5 sm:mb-1 text-[10px] sm:text-xs">
            {day.format('D')}
          </div>
          {checkin && (
            <>
              <div className={`w-full h-1 sm:h-1.5 md:h-2 mt-0.5 sm:mt-1 rounded ${statusColor}`} />
              <div className="mt-0.5 sm:mt-1 md:mt-2 text-[8px] sm:text-[10px] md:text-xs">
                <div className="text-gray-600">{dayjs(checkin.created_at).format('HH:mm')}</div>
                <div>{statusEmoji}</div>
                {checkin.meal_allowance > 0 && <div>🍽️</div>}
              </div>
            </>
          )}
        </div>
      );

      if (days.length === 7) {
        weeks.push(
          <div key={day.format('YYYY-MM-DD')} className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
            {days}
          </div>
        );
        days = [];
      }

      day = day.add(1, 'day');
    }

    if (days.length > 0) {
      weeks.push(
        <div key={day.format('YYYY-MM-DD')} className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {days}
        </div>
      );
    }

    return weeks;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm md:shadow p-2 sm:p-4 md:p-6 mb-4 sm:mb-6">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <button 
          className="bg-gray-100 border border-gray-200 rounded p-1 sm:p-2 cursor-pointer hover:bg-gray-200"
          onClick={() => setCurrentDate(currentDate.subtract(1, 'month'))}
        >
          ◀
        </button>
        <div className="text-base sm:text-lg font-semibold text-gray-800">
          {currentDate.format('MMMM YYYY')}
        </div>
        <button 
          className="bg-gray-100 border border-gray-200 rounded p-1 sm:p-2 cursor-pointer hover:bg-gray-200"
          onClick={() => setCurrentDate(currentDate.add(1, 'month'))}
        >
          ▶
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-gray-500 text-xs font-medium">
            {day}
          </div>
        ))}
      </div>

      {renderCalendar()}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-2 md:gap-3 mt-3 sm:mt-4 text-xs sm:text-sm">
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded bg-green-800" />
          <span>Perfect (Before 8:00)</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded bg-green-500" />
          <span>On Time (8:00-8:10)</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded bg-amber-500" />
          <span>Late (8:10-8:30)</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded bg-red-500" />
          <span>Late (after 8:30)</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded bg-blue-500" />
          <span>Exemption Used</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <span className="text-base">🍽️</span>
          <span>Meal Allowance</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView; 